import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { ConfirmDialog } from './ConfirmDialog';

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /**
   * Action à exécuter à la confirmation. Tant qu'elle court, le dialogue reste
   * ouvert avec son bouton en chargement (pas de double clic possible) ; il se
   * ferme à la fin. Si elle échoue, l'erreur remonte à l'appelant et le
   * dialogue se ferme. Sans `run`, la confirmation se contente de répondre.
   */
  run?: () => Promise<unknown> | unknown;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

export const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Confirmation applicative à la demande : `const confirm = useConfirm()` puis
 * `if (!(await confirm({ title: '…', danger: true }))) return;`.
 * Remplace les window.confirm() natifs par le dialogue standard de l'app.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const resolverRef = useRef<{
    resolve: (value: boolean) => void;
    reject: (reason: unknown) => void;
  } | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve, reject) => {
      resolverRef.current = { resolve, reject };
      setOptions(opts);
    });
  }, []);

  function close() {
    resolverRef.current = null;
    setOptions(null);
    setLoading(false);
  }

  async function onConfirm() {
    const pending = resolverRef.current;
    if (!pending) return;
    if (!options?.run) {
      pending.resolve(true);
      close();
      return;
    }
    setLoading(true);
    try {
      await options.run();
      pending.resolve(true);
    } catch (error) {
      pending.reject(error);
    } finally {
      close();
    }
  }

  function onCancel() {
    if (loading) return;
    resolverRef.current?.resolve(false);
    close();
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={options !== null}
        title={options?.title ?? ''}
        message={options?.message}
        confirmLabel={options?.confirmLabel}
        cancelLabel={options?.cancelLabel}
        danger={options?.danger}
        loading={loading}
        onConfirm={() => void onConfirm()}
        onCancel={onCancel}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
}

/** Variante tolérante : `null` hors provider (composants testés isolément). */
export function useOptionalConfirm(): ConfirmFn | null {
  return useContext(ConfirmContext);
}
